// --- Constants ---
export const MAX_POPULATION = 300;
export const DEFAULT_DOT_COLOR = '#9ca3af';
export const DEFAULT_DOT_RADIUS = 1.2;
export const SELECTED_DOT_RADIUS = 1.8;

const SVG_VIEWBOX_SIZE = 100;
const SVG_PADDING = 5;
const USABLE_SIZE = SVG_VIEWBOX_SIZE - 2 * SVG_PADDING;

/** SVG coordinate extent for the biased-sampling zone (top-left corner). */
export const BIAS_ZONE_EXTENT = 33;

// --- Config interfaces ---

export type SampleMethod = 'random' | 'systematic' | 'stratified' | 'biased';

export interface DemographicCategory {
  label: string;
  percentage: number; // 0-100
  color: string; // hex color
}

export interface ReliabilityBar {
  label: string;
  width: number; // percentage 0-100
  color: string;
}

export interface PopulationWidgetParams {
  populationSize: number; // 0 to MAX_POPULATION

  // Base dot appearance
  dotOpacity?: number; // default 1

  // Demographics (single dimension)
  demographicName?: string;
  demographicCategories?: DemographicCategory[];

  // Sample selection
  sampleMethod?: SampleMethod;
  sampleColor?: string;
  sampleSeed?: number; // for random / stratified (default 42)
  samplePercentage?: number; // percentage of population to sample (preferred over sampleSize)
  sampleSize?: number; // absolute count (fallback when samplePercentage is not set)
  sampleInterval?: number; // for systematic: every Nth item (default 10)

  // Overlays
  showStratifiedDivider?: boolean;
  showBiasZone?: boolean;

  // Reliability bars
  reliabilityBars?: ReliabilityBar[];
}

// --- Internal dot state ---

export interface PopulationDot {
  id: number;
  cx: number;
  cy: number;
  r: number;
  color: string;
  opacity: number;
}

// --- Grid computation ---

export function computeGridDimensions(size: number): { cols: number; rows: number } {
  if (size <= 0) {
    return { cols: 1, rows: 1 };
  }
  const cols = Math.ceil(Math.sqrt(size));
  const rows = Math.ceil(size / cols);
  return { cols, rows };
}

// --- Demographic helpers ---

export function normalizePercentages(categories: DemographicCategory[]): DemographicCategory[] {
  if (categories.length === 0) {
    return [];
  }
  const total = categories.reduce((sum, c) => sum + c.percentage, 0);
  if (total === 0) {
    const even = 100 / categories.length;
    return categories.map((c) => ({ ...c, percentage: even }));
  }
  const scale = 100 / total;
  return categories.map((c) => ({ ...c, percentage: c.percentage * scale }));
}

export function assignCategory(
  dotIndex: number,
  totalActive: number,
  categories: DemographicCategory[],
): DemographicCategory | undefined {
  if (categories.length === 0 || totalActive === 0) {
    return undefined;
  }
  const fraction = dotIndex / totalActive;
  let cumulative = 0;
  for (const cat of categories) {
    cumulative += cat.percentage / 100;
    if (fraction < cumulative) {
      return cat;
    }
  }
  return categories[categories.length - 1];
}

// --- Selection algorithms ---

export function seededRandomSelection(total: number, count: number, seed: number): number[] {
  const indices = Array.from({ length: total }, (_, i) => i);
  let s = seed;
  for (let i = indices.length - 1; i > 0; i--) {
    s = (s * 16807) % 2147483647;
    const j = s % (i + 1);
    [indices[i], indices[j]] = [indices[j], indices[i]];
  }
  return indices.slice(0, count);
}

export function systematicSelection(total: number, interval: number, start = 0): number[] {
  const result: number[] = [];
  for (let i = start; i < total; i += interval) {
    result.push(i);
  }
  return result;
}

export function stratifiedSelection(
  total: number,
  count: number,
  categories: DemographicCategory[],
  seed = 42,
): number[] {
  if (categories.length === 0) {
    return seededRandomSelection(total, count, seed);
  }
  const normalized = normalizePercentages(categories);
  const result: number[] = [];
  let dotStart = 0;

  for (const cat of normalized) {
    const groupSize = Math.round((total * cat.percentage) / 100);
    const sampleFromGroup = Math.round((count * cat.percentage) / 100);
    // Randomly sample within each stratum (not just the first N)
    const selected = seededRandomSelection(groupSize, sampleFromGroup, seed + dotStart);
    for (const idx of selected) {
      result.push(dotStart + idx);
    }
    dotStart += groupSize;
  }
  return result.slice(0, count);
}

interface BaseDot {
  id: number;
  cx: number;
  cy: number;
}

export function biasedSelection(dots: BaseDot[], count: number): number[] {
  return dots
    .filter((d) => d.cx < BIAS_ZONE_EXTENT && d.cy < BIAS_ZONE_EXTENT)
    .map((d) => d.id)
    .slice(0, count);
}

// --- Main computation ---

function computeBaseDots(size: number): BaseDot[] {
  const { cols, rows } = computeGridDimensions(size);
  const dots: BaseDot[] = [];
  for (let i = 0; i < size; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    dots.push({
      id: i,
      cx: SVG_PADDING + ((col + 0.5) / cols) * USABLE_SIZE,
      cy: SVG_PADDING + ((row + 0.5) / rows) * USABLE_SIZE,
    });
  }
  return dots;
}

function getEffectiveSampleSize(params: PopulationWidgetParams): number | undefined {
  if (params.samplePercentage !== null && params.samplePercentage !== undefined) {
    return Math.round((params.populationSize * params.samplePercentage) / 100);
  }
  return params.sampleSize;
}

function computeSelectedSet(baseDots: BaseDot[], params: PopulationWidgetParams): Set<number> | undefined {
  const { sampleMethod } = params;
  if (!sampleMethod) {
    return undefined;
  }

  const size = baseDots.length;
  const seed = params.sampleSeed ?? 42;
  let indices: number[];

  switch (sampleMethod) {
    case 'random': {
      const count = getEffectiveSampleSize(params);
      if (!count) {
        return undefined;
      }
      indices = seededRandomSelection(size, count, seed);
      break;
    }
    case 'systematic': {
      const interval = params.sampleInterval ?? 10;
      indices = systematicSelection(size, interval);
      break;
    }
    case 'stratified': {
      const count = getEffectiveSampleSize(params);
      if (!count) {
        return undefined;
      }
      const categories = params.demographicCategories;
      indices = categories?.length
        ? stratifiedSelection(size, count, categories, seed)
        : seededRandomSelection(size, count, seed);
      break;
    }
    case 'biased': {
      const count = getEffectiveSampleSize(params);
      if (!count) {
        return undefined;
      }
      indices = biasedSelection(baseDots, count);
      break;
    }
    default:
      return undefined;
  }

  return new Set(indices);
}

export function computeDots(params: PopulationWidgetParams): PopulationDot[] {
  const size = Math.max(0, Math.min(MAX_POPULATION, Math.round(params.populationSize)));
  const baseDots = computeBaseDots(size);
  const normalizedCategories = params.demographicCategories?.length
    ? normalizePercentages(params.demographicCategories)
    : undefined;

  const selectedSet = computeSelectedSet(baseDots, params);
  const baseOpacity = params.dotOpacity ?? 1;
  const hasSample = !!selectedSet;
  const dots: PopulationDot[] = [];

  for (let i = 0; i < MAX_POPULATION; i++) {
    if (i < size) {
      const base = baseDots[i];
      const isSelected = selectedSet?.has(i) ?? false;
      const category = normalizedCategories ? assignCategory(i, size, normalizedCategories) : undefined;

      let color: string;
      let r: number;
      let opacity: number;

      if (isSelected) {
        color = category?.color ?? params.sampleColor ?? '#22c55e';
        r = SELECTED_DOT_RADIUS;
        opacity = 1;
      } else if (hasSample) {
        color = category?.color ?? DEFAULT_DOT_COLOR;
        r = DEFAULT_DOT_RADIUS;
        opacity = 0.35;
      } else {
        color = category?.color ?? DEFAULT_DOT_COLOR;
        r = DEFAULT_DOT_RADIUS;
        opacity = baseOpacity;
      }

      dots.push({ id: i, cx: base.cx, cy: base.cy, r, color, opacity });
    } else {
      dots.push({
        id: i,
        cx: SVG_VIEWBOX_SIZE / 2,
        cy: SVG_VIEWBOX_SIZE / 2,
        r: 0,
        color: DEFAULT_DOT_COLOR,
        opacity: 0,
      });
    }
  }

  return dots;
}
